// Shared constants for the Campus Quad ZoneScene

export const COLORS = {
  navyDeep: "#0b1530",
  navy: "#0f1f4a",
  navyMid: "#16315a",
  gold: "#e6b450",
  goldBright: "#f7d27a",
  stone: "#8d8576",
  stoneLight: "#b3a994",
  stoneDark: "#5a5347",
  grass: "#3f6b3a",
  grassDark: "#2c4e29",
  bark: "#4a3526",
  leaves: "#2e5f3a",
  brick: "#6b3a2e",
  brickRoof: "#1a2747",
  woodBench: "#5a3e22",
  water: "#1d3a6b",
  ivory: "#f3ead4",
  twilightTop: "#0a1330",
  twilightHorizon: "#d6914a",
};

export const DAY_COLORS = {
  skyTop: "#3d7fc5",
  skyMid: "#7eb4e8",
  skyHorizon: "#fbe4b8",
  fogNear: "#cfe1f5",
  ambient: "#e6efff",
  hemiSky: "#bedcff",
  hemiGround: "#7d8a55",
  sun: "#fff4d6",
  warmFill: "#ffe7b0",
};

export const QUAD_SIZE = 60;

// Hotspots — id maps to the app route under /landmark/:id
export const HOTSPOTS = [
  {
    id: "boardroom",
    label: "Boardroom Hall",
    description:
      "Where the cohort meets, weekly, to think out loud — together.",
    position: [0, 1.6, -23],
    route: "/landmark/boardroom",
  },
  {
    id: "library",
    label: "Great Library",
    description:
      "Twelve thousand volumes. No fiction. No second editions when a first is available.",
    position: [0, 1.6, 23],
    route: "/landmark/library",
  },
  {
    id: "lecture",
    label: "Lecture Hall",
    description:
      "Tuesday evenings. One member, one hour, one argument not yet finished.",
    position: [23, 1.6, 0],
    route: "/landmark/lecture",
  },
  {
    id: "bell",
    label: "Bell Tower",
    description: "Cast 1908. Rehung 1962. Rung every hour since.",
    position: [-23, 1.6, 0],
    route: "/landmark/bell",
  },
];

export const BELL_TOWER_WORLD_POS = [-23, 6, 0];

// Crowd density slider (manual override of NPC count). When unset / null,
// NPCs auto-scale with day/night (50–100). When a number is stored, that
// value is used verbatim (clamped between MIN and MAX).
export const CROWD_DENSITY_STORAGE_KEY = "tab-zone-crowd-density";
export const CROWD_DENSITY_MIN = 25;
export const CROWD_DENSITY_MAX = 150;

// Points of Interest — smaller info markers scattered around the Quad.
// Same visual style as HOTSPOTS but they DON'T navigate anywhere; clicking
// just shows a 2-sentence flavour blurb in the existing hotspot panel.
//
// IMPORTANT: placeholder copy — replace before launch.
export const POINTS_OF_INTEREST = [
  {
    id: "poi-founders-fountain",
    label: "The Founder's Fountain",
    description:
      "The heart of the quad since 2026, its three concentric basins represent inquiry, debate, and resolve. Members toss a coin and a question.",
    position: [2.6, 1.4, 1.8],
  },
  {
    id: "poi-scholars-reflection",
    label: "Scholar's Reflection",
    description:
      "A small marble plaque set into the paving stones, etched with the words of the first cohort. It catches the morning sun before any other point on the quad.",
    position: [-2.6, 1.4, -1.8],
  },
  {
    id: "poi-philosophers-bench",
    label: "The Philosopher's Bench",
    description:
      "A quiet spot for reflection, polished smooth by ten thousand long arguments. The view runs straight down the eastern colonnade.",
    position: [12, 1.4, 1.7],
  },
  {
    id: "poi-readers-corner",
    label: "The Reader's Corner",
    description:
      "Sheltered from the wind by a low yew hedge, this is where members come to finish chapters they meant to finish years ago. The lamp overhead burns until midnight.",
    position: [-12, 1.4, 1.7],
  },
  {
    id: "poi-rose-garden",
    label: "The Rose Garden",
    description:
      "Thirty-six varietals, each donated by a different founding member. They bloom in waves from May until first frost.",
    position: [17, 1.4, 17],
  },
  {
    id: "poi-oak-grove",
    label: "The Oak Grove",
    description:
      "Three sister oaks planted on the day the cornerstone was laid. Each is named for a virtue the founders argued about for an entire summer.",
    position: [-17, 1.4, 17],
  },
  {
    id: "poi-memorial-walk",
    label: "The Memorial Walk",
    description:
      "A line of paving stones inscribed with the names of teachers we wish we had asked one more question. Trace it from end to end on your way to the lecture hall.",
    position: [17, 1.4, -17],
  },
  {
    id: "poi-sundial-court",
    label: "The Sundial Court",
    description:
      "An octagonal court of pale limestone, with a bronze gnomon that has kept honest time since installation. It does not adjust for daylight savings.",
    position: [-17, 1.4, -17],
  },
  {
    id: "poi-boardroom-approach",
    label: "The Boardroom Approach",
    description:
      "Eight broad steps leading to the door of the Boardroom Hall, deliberately wide so members arrive walking shoulder to shoulder. The brass handrails are warm even in December.",
    position: [5, 1.4, -18],
  },
  {
    id: "poi-library-steps",
    label: "The Library Steps",
    description:
      "Where members trade contraband fiction and unread first editions before opening hours. The third step from the top is the one that creaks.",
    position: [-5, 1.4, 18],
  },
];

// localStorage keys
export const TIME_MODE_STORAGE_KEY = "tab-zone-time-mode";
export const BELL_MUTED_STORAGE_KEY = "tab-zone-bell-muted";
export const BELL_VOLUME_STORAGE_KEY = "tab-zone-bell-volume";

// URL query parameter keys (for Share-this-view)
export const URL_PARAMS = {
  cam: "cam",
  target: "tg",
  time: "t",
};

// Default orbit camera pose
export const DEFAULT_CAM_POS = [28, 22, 32];
export const DEFAULT_CAM_TARGET = [0, 2, 0];
